import { PMGradingRow } from "../../workers/pmui";

/**
 * Represents the state of the production form used in the PM grading process.
 *
 * @property engineer - The name or identifier of the engineer handling the form.
 * @property doesProductMatchLink - Indicates if the product matches the provided link.
 * @property suspectedReasonForReturn - The suspected reason for the product's return.
 * @property assessmentFaultCode - The fault code determined during assessment.
 * @property listingComments - Additional comments related to the product listing.
 * @property productImages - URLs or identifiers for product images.
 * @property grade - The grade assigned to the product after assessment.
 * @property disposalReason - The reason for disposing of the product, if applicable.
 * @property productionNumberOfBoxes - The number of boxes used in production packaging.
 * @property productionDescriptionOfPackaging - Description of the packaging used in production.
 * @property isShippingSuitable - Indicates if the product is suitable for shipping.
 * @property spareParts - Information about any spare parts included or required.
 * @property postProductionExit - Notes or status after production exit.
 * @property packagingShippingService - The shipping service used for packaging.
 * @property outboundShippingService - The outbound shipping service used for delivery.
 */
export type ProductionFormState = {
  /**
   * The name or identifier of the engineer handling the form.
   */
  engineer: string;
  /**
   * Indicates if the product matches the provided link.
   */
  doesProductMatchLink: boolean;
  /**
   * The suspected reason for the product's return.
   */
  suspectedReasonForReturn: string;
  /**
   * The fault code determined during assessment.
   */
  assessmentFaultCode: string;
  /**
   * Additional comments related to the product listing.
   */
  listingComments: string;
  /**
   * URLs or identifiers for product images.
   */
  productImages: string;
  /**
   * The grade assigned to the product after assessment.
   */
  grade: string;
  /**
   * The reason for disposing of the product, if applicable.
   */
  disposalReason: string;
  /**
   * The number of boxes used in production packaging.
   */
  productionNumberOfBoxes: number;
  /**
   * Description of the packaging used in production.
   */
  productionDescriptionOfPackaging: string;
  /**
   * Indicates if the shipping chosen is suitable.
   */
  isShippingSuitable: boolean;
  /**
   * Information about any spare parts included or required.
   */
  spareParts: string;
  /**
   * Exit after production.
   */
  postProductionExit: string;
  /**
   * The shipping service used for packaging.
   */
  packagingShippingService: string;
  /**
   * The outbound shipping service used for delivery.
   */
  outboundShippingService: string;

  /**
   * Client code
   */
  clientCode: string;

  /**
   * Pallet number from manifest for tracking and storage
   */
  refurbPalletNumber: string;
};

export interface ProductionFormStateWithFrontEndOnlyFields
  extends ProductionFormState {
  disposal: boolean;
  /**
   * Indicates whether images have been uploaded for this item
   */
  hasImages: boolean;
}

/**
 * Flattens the production data from a `PMGradingRow` object into a partial `ProductionFormState` object.
 *
 * This function extracts relevant fields from the `production` property of the given row,
 * providing default values where necessary. Boolean fields are normalized from string values
 * (e.g., "Yes" to `true`). If the input row or its production data is undefined, an empty object is returned.
 *
 * @param row - The `PMGradingRow` object containing production data to be flattened.
 * @returns A partial `ProductionFormState` object with flattened and normalized production data.
 */
export function flattenProductionData(
  row: PMGradingRow | undefined
): Partial<ProductionFormState> {
  if (!row || !row.production) return {};
  const prod = row.production;
  return {
    clientCode: row.manifest.clientCode,
    engineer: prod.engineer || "",
    doesProductMatchLink:
      typeof prod.doesProductMatchLink === "boolean"
        ? prod.doesProductMatchLink
        : prod.doesProductMatchLink?.toLowerCase() === "yes",
    suspectedReasonForReturn: prod.suspectedReasonForReturn || "",
    assessmentFaultCode: prod.assessmentFaultCode || "",
    listingComments: prod.listingComments || "",
    productImages: prod.productImages || "",
    grade: prod.grade || "",
    disposalReason: prod.disposalReason || "",
    productionNumberOfBoxes:
      typeof prod.productionNumberOfBoxes === "number"
        ? prod.productionNumberOfBoxes
        : Number(prod.productionNumberOfBoxes) || 0,
    productionDescriptionOfPackaging:
      prod.productionDescriptionOfPackaging || "",
    isShippingSuitable:
      typeof prod.isShippingSuitable === "boolean"
        ? prod.isShippingSuitable
        : prod.isShippingSuitable?.toLowerCase() === "yes",
    spareParts: prod.spareParts || "",
    postProductionExit: prod.postProductionExit || "",
    packagingShippingService: prod.packagingShippingService,
    outboundShippingService: prod.outboundShippingService,
    refurbPalletNumber: row?.outbound?.palletNumber || "",
  };
}

/**
 * Transforms a partial `ProductionFormState` object into a production data object
 * with specific formatting for certain fields.
 *
 * - Converts boolean fields (`doesProductMatchLink`, `isShippingSuitable`) to "Yes" or "No" strings.
 * - Passes through other fields as-is.
 *
 * @param form - A partial object representing the production form state.
 * @returns An object containing the formatted production data.
 */
export function toProductionData(form: Partial<ProductionFormState>) {
  return {
    engineer: form.engineer,
    doesProductMatchLink: form.doesProductMatchLink ? "Yes" : "No",
    suspectedReasonForReturn: form.suspectedReasonForReturn,
    assessmentFaultCode: form.assessmentFaultCode,
    listingComments: form.listingComments,
    productImages: form.productImages,
    grade: form.grade,
    disposalReason: form.disposalReason,
    productionNumberOfBoxes: form.productionNumberOfBoxes,
    productionDescriptionOfPackaging: form.productionDescriptionOfPackaging,
    isShippingSuitable: form.isShippingSuitable ? "Yes" : "No",
    spareParts: form.spareParts,
    postProductionExit: form.postProductionExit,
    packagingShippingService: form.packagingShippingService,
    outboundShippingService: form.outboundShippingService,
    clientCode: form.clientCode,
    refurbPalletNumber: form.refurbPalletNumber,
  };
}

export function createListingSku(row: {
  catalogueExit: string;
  sku: string;
  grade: string;
  pmid: string;
  clientPrefix: string;
}): string {
  const { catalogueExit, sku, grade, pmid, clientPrefix } = row;

  // Check if L is either "Retail" or "Auction"
  if (catalogueExit !== "Retail" && catalogueExit !== "Auction") {
    return "";
  }

  // Simulate HLOOKUP: C is the lookup key, return the mapped value from some object
  let result = `${clientPrefix}-${sku}`;

  if (grade !== "") {
    result += `-${grade}`;
    if (grade === "BC" || grade === "AC") {
      // Remove `[A-Z-]*\.[a-z-]|[A-Z-]` from F
      const strippedF = pmid.replace(/[A-Z-]*\.[a-z-]|[A-Z-]/g, "");
      result += strippedF;
    }
  }

  return result;
}

export const formatDateAsHumanReadable = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-based in JS
  const year = date.getFullYear();

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};
